import { useState } from 'react';
import type { AuditDetail } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import { Archive, ArchiveRestore, Loader2 } from 'lucide-react';

interface AuditArchivedBannerProps {
  audit: AuditDetail;
  /** Customer admin: may restore. */
  canRestore: boolean;
  /** Resolves null on success, or the reason it failed (shown in the banner). */
  onRestore: () => Promise<string | null>;
}

/** Shown on an archived audit: when and by whom, read-only until a customer admin restores it. */
export default function AuditArchivedBanner({ audit, canRestore, onRestore }: AuditArchivedBannerProps) {
  const { formatDate } = useFormatters();
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const restore = async () => {
    setBusy(true);
    setError(null);
    const failure = await onRestore();
    setBusy(false);
    if (failure) setError(failure);
  };

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40" role="status"
      data-testid="audit-archived-banner">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-medium">
          <Archive className="h-4 w-4 shrink-0 text-amber-600" />
          <span>
            Archived{audit.archived_at ? ` ${formatDate(audit.archived_at)}` : ''}
            {audit.archived_by_name ? ` by ${audit.archived_by_name}` : ''} — read-only.
            {canRestore ? ' Restore it to make changes.' : ''}
          </span>
        </p>
        {canRestore && (
          <Button variant="outline" className="h-11" onClick={() => void restore()} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArchiveRestore className="h-4 w-4 mr-1.5" />}
            Restore
          </Button>
        )}
      </div>
      {error && <p role="alert" className="mt-2 text-sm text-destructive">{error}</p>}
    </div>
  );
}
